import axios from "@/shared/scripts/AutoRefreshAxios";
import store from "../store";
import { Classified } from "@/shared/interfaces/classified/Classified.interface";
import { Pricing } from "@/shared/interfaces/pricing/pricing.interface";
import { getDurationInDays } from "@/shared/scripts/helpers/date";

interface GetPricingOptions {
    classified?: Classified | null,
    type?: string,
    propertyType?: string;
    transactionType?: string;
    price?: string;
    preferShortestRentDuration?: boolean;
}

// Pick the default pack to pre-select from the duration-sorted pricing list.
// FOR_RENT and FOR_SALE both default to the 2-month pack; when the caller
// prefers the shortest duration we take the 1-month pack for sale (or the very shortest
// available for rent). Falls back to the shortest pack when the preferred one is absent.
export function selectDefaultPricing(
    orderByData: Array<Pricing>,
    transactionType: string,
    preferShortestRentDuration: boolean
): Pricing | null {
    if (!orderByData.length) {
        return null;
    }

    if (transactionType === "FOR_RENT") {
        if (preferShortestRentDuration) {
            return orderByData[0];
        }
        return orderByData.find(
            (p: Pricing) => p.duration.unit === "MONTH" && p.duration.count === 2
        ) || orderByData[0];
    }

    if (preferShortestRentDuration) {
        return orderByData.find(
            (p: Pricing) => p.duration.unit === "MONTH" && p.duration.count === 1
        ) || orderByData[0];
    }
    return orderByData.find(
        // IO-277: Change FSBO default duration from 4 to 2 months
        (p: Pricing) => p.duration.unit === "MONTH" && p.duration.count === 2
    ) || orderByData[0];
}

export function getPricing(options: GetPricingOptions = {}): Promise<Array<Pricing>> {
    let classifiedId: string;
    let propertyType: string;
    let transactionType: string;
    let price: string;

    if (options.classified) {
        classifiedId = options.classified?.id;
        propertyType = options.classified?.property.type;
        transactionType = options.classified?.transaction.type;
        price = transactionType === "FOR_SALE"
            ? options.classified?.transaction.sale.price
            : options.classified?.transaction.rental.monthlyRentalPrice as unknown as string;
    } else {
        propertyType = store.getters["FormModule/getValue"]("property.type");
        transactionType = store.getters["FormModule/getValue"]("transaction.type");
        price = store.getters["FormModule/getValue"](transactionType === "FOR_SALE" ? "transaction.sale.price" : "transaction.rental.monthlyRentalPrice");
        classifiedId = store.getters["FormModule/getClassifiedId"];
    }

    // if PropertyType or TransactionType or Price is null we cannot ask the correct pricing
    if (propertyType == null || transactionType == null || price == null) {
        return Promise.reject("Cannot call the services pricing with propertyType :" + propertyType + " transactionType :" + transactionType + " price " + price);
    }

    const pricingType = options.type ?? "PUBLICATION";

    const uri = "/?propertyType=" + propertyType + "&transactionType=" + transactionType + "&price=" + price + "&pricingType=" + pricingType + "&classifiedId=" + classifiedId;

    // API settings
    const baseUrl: string = process.env.VUE_APP_PRICING_URL || "";

    const version = "2";
    const method = "get";
    const endpoint = "pricing/";

    return axios({
        method: method,
        url: baseUrl + endpoint + version + uri,
        headers: {
            "x-api-key": process.env.VUE_APP_PRICING_API_KEY,
        }
    }).then(res => {
        if(res.data) {
            const orderByData = [...res.data].sort((a, b) => getDurationInDays(a) - getDurationInDays(b));
            store.commit("FormModule/addValueTransaction", {"path": "transaction.packages", "value": orderByData});
            store.commit("FormModule/addValueOrder", {"path": "order.classifiedId", "value": classifiedId});
            const pricing = selectDefaultPricing(orderByData, transactionType, !!options.preferShortestRentDuration);

            if (pricing) {
                store.commit("FormModule/addValueTransaction", {"path": "transaction.duration", "value": pricing.id});

                // Set default pack (if new classified) or chosen pack (if package upgrade).
                let visualization = options.classified?.publication?.visualizationOption || "Classic";
                visualization = visualization.charAt(0).toUpperCase() + visualization.slice(1).toLowerCase();

                store.commit("FormModule/addValueTransaction", { "path": "transaction.visualizationOption", "value": visualization });
                store.commit("FormModule/addValueTransaction", {"path": "transaction.pricing.basic", "value": pricing.visualizationOptions.basic});
                store.commit("FormModule/addValueTransaction", {"path": "transaction.pricing.classic", "value": pricing.visualizationOptions.classic});
                store.commit("FormModule/addValueTransaction", {"path": "transaction.pricing.premium", "value": pricing.visualizationOptions.premium});
                store.commit("FormModule/addValueTransaction", { "path": "transaction.pricing.extensionOptionDetails", value: pricing.extensionOption});
                store.commit("FormModule/addValueTransaction", { "path": "transaction.pricing.extensionOption",value: null});

                if (pricing.billboard) {
                    Object.entries(pricing.billboard).forEach(([key, value]) => {
                        const packType = key.replace("Price", "");

                        store.commit("FormModule/addValueTransaction", {
                            path: `transaction.pricing.billboard.${packType}`,
                            value: value
                        });
                    });
                }
                store.commit("FormModule/addValueUI", { "path": "transaction.pack", "value": pricing });
            }
            return res.data;
        }
    }).catch(error => {
        console.error("Got error while uploading picture", error);
        throw new Error(error);
    });

}
